import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, Settings, Globe, AlertTriangle } from 'lucide-react';

interface CookieConsentProps {
  onAccept: () => void;
  onDecline: () => void;
  onCustomize: () => void;
}

interface ConsentSettings {
  essential: boolean;
  analytics: boolean;
  advertising: boolean;
  functional: boolean;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({
  onAccept,
  onDecline,
  onCustomize
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [consentSettings, setConsentSettings] = useState<ConsentSettings>({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    advertising: false,
    functional: false
  });

  // Check if user is in EEA, UK, or Switzerland
  const isEEAUser = () => {
    const eeaCountries = [
      'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 
      'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
      'GB', 'CH' // UK and Switzerland
    ];
    
    // Try to get country from IP or browser locale
    const userCountry = navigator.language?.split('-')[1] || 'US';
    return eeaCountries.includes(userCountry.toUpperCase());
  };

  useEffect(() => {
    const consent = localStorage.getItem('gdpr-consent');
    const userLocation = localStorage.getItem('user-location');
    
    // Check if user is in EEA/UK/CH and hasn't given consent
    if (isEEAUser() && !consent) {
      setIsVisible(true);
      localStorage.setItem('user-location', 'EEA');
    } else if (!consent && !userLocation) {
      // For non-EEA users, show a simplified version
      setIsVisible(true);
      localStorage.setItem('user-location', 'NON-EEA');
    }
  }, []);

  const handleAccept = () => {
    const fullConsent = {
      essential: true,
      analytics: true,
      advertising: true,
      functional: true,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem('gdpr-consent', JSON.stringify(fullConsent));
    localStorage.setItem('cookie-consent', 'accepted');
    setIsVisible(false);
    onAccept();
  };

  const handleDecline = () => {
    const minimalConsent = {
      essential: true,
      analytics: false,
      advertising: false,
      functional: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem('gdpr-consent', JSON.stringify(minimalConsent));
    localStorage.setItem('cookie-consent', 'declined');
    setIsVisible(false);
    onDecline();
  };

  const handleCustomize = () => {
    setShowCustomize(!showCustomize);
    onCustomize();
  };

  const handleSaveCustomSettings = () => {
    const customConsent = {
      ...consentSettings,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem('gdpr-consent', JSON.stringify(customConsent));
    localStorage.setItem('cookie-consent', 'customized');
    setIsVisible(false);
    onAccept();
  };

  const handleSettingChange = (setting: keyof ConsentSettings) => {
    if (setting === 'essential') return; // Essential cannot be disabled
    setConsentSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  if (!isVisible) return null;

  const isEEA = isEEAUser();

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg">
      <div className="max-w-7xl mx-auto p-3 sm:p-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 lg:gap-4">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 sm:gap-3">
              <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                    {isEEA ? 'Privacy & Cookie Consent' : 'We use cookies to enhance your experience'}
                  </h3>
                  {isEEA && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                      <Globe className="w-3 h-3" />
                      <span>GDPR</span>
                    </div>
                  )}
                </div>
                
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mb-2 sm:mb-3 leading-relaxed">
                  {isEEA ? (
                    <>
                      We use cookies and similar technologies to provide our free service, analyze usage, and show personalized ads. 
                      Under GDPR, we need your consent to use non-essential cookies. 
                      <strong> Essential cookies are required for basic functionality and cannot be disabled.</strong>
                    </>
                  ) : (
                    <>
                      We use cookies and similar technologies to provide, protect, and improve our services. 
                      This includes analytics to understand how you use our site and advertising to support our free service.
                    </>
                  )}
                </p>
                
                {showDetails && (
                  <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 sm:mb-3 text-sm">Cookie Types We Use:</h4>
                    <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                          <span className="font-medium text-gray-900 dark:text-white">Essential Cookies</span>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Required</span>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 ml-5 sm:ml-6">
                          Required for basic functionality, security, and site operation. Cannot be disabled.
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Settings className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                          <span className="font-medium text-gray-900 dark:text-white">Analytics Cookies</span>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Optional</span>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 ml-5 sm:ml-6">
                          Help us understand how you use our site to improve our services and user experience.
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Cookie className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                          <span className="font-medium text-gray-900 dark:text-white">Advertising Cookies</span>
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">Optional</span>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 ml-5 sm:ml-6">
                          Used to display relevant ads and support our free service. Helps keep our games free for everyone.
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Settings className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" />
                          <span className="font-medium text-gray-900 dark:text-white">Functional Cookies</span>
                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded">Optional</span>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 ml-5 sm:ml-6">
                          Enable enhanced functionality and personalization, such as remembering your preferences.
                        </p>
                      </div>
                    </div>
                    
                    {isEEA && (
                      <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-blue-800 dark:text-blue-200">
                            <strong>GDPR Notice:</strong> You have the right to withdraw consent at any time. 
                            You can update your preferences in our{' '}
                            <a href="/help/privacy-policy" className="underline hover:no-underline">
                              Privacy Policy
                            </a>.
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-3 sm:mt-4 text-xs text-gray-500 dark:text-gray-400">
                      By clicking "Accept All", you consent to our use of cookies. 
                      You can learn more in our{' '}
                      <a href="/help/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:underline">
                        Privacy Policy
                      </a>.
                    </div>
                  </div>
                )}

                {showCustomize && (
                  <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3 text-sm">Customize Your Privacy Settings:</h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-green-600" />
                          <span className="font-medium">Essential Cookies</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Always Active</span>
                          <div className="w-10 h-6 bg-green-600 rounded-full flex items-center justify-end px-1">
                            <div className="w-4 h-4 bg-white rounded-full"></div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Settings className="w-4 h-4 text-blue-600" />
                          <span className="font-medium">Analytics Cookies</span>
                        </div>
                        <button
                          onClick={() => handleSettingChange('analytics')}
                          className={`w-10 h-6 rounded-full flex items-center transition-colors ${
                            consentSettings.analytics ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'
                          } px-1`}
                        >
                          <div className="w-4 h-4 bg-white rounded-full"></div>
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Cookie className="w-4 h-4 text-purple-600" />
                          <span className="font-medium">Advertising Cookies</span>
                        </div>
                        <button
                          onClick={() => handleSettingChange('advertising')}
                          className={`w-10 h-6 rounded-full flex items-center transition-colors ${
                            consentSettings.advertising ? 'bg-purple-600 justify-end' : 'bg-gray-300 justify-start'
                          } px-1`}
                        >
                          <div className="w-4 h-4 bg-white rounded-full"></div>
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Settings className="w-4 h-4 text-orange-600" />
                          <span className="font-medium">Functional Cookies</span>
                        </div>
                        <button
                          onClick={() => handleSettingChange('functional')}
                          className={`w-10 h-6 rounded-full flex items-center transition-colors ${
                            consentSettings.functional ? 'bg-orange-600 justify-end' : 'bg-gray-300 justify-start'
                          } px-1`}
                        >
                          <div className="w-4 h-4 bg-white rounded-full"></div>
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={handleSaveCustomSettings}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        Save Settings
                      </button>
                      <button
                        onClick={() => setShowCustomize(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-row flex-wrap gap-2 sm:gap-3 w-full lg:w-auto lg:flex-shrink-0">
            <button
              onClick={handleCustomize}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors whitespace-nowrap"
            >
              Customize
            </button>
            <button
              onClick={handleDecline}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors whitespace-nowrap"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors whitespace-nowrap"
            >
              Accept All
            </button>
            <button
              onClick={() => setIsVisible(false)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
